import React, { useCallback, useEffect, useState } from 'react';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import { Outlet, NavLink, useNavigate, Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout, selectCurrentUser } from '../../features/auth/authSlice';
import {
  HomeIcon,
  ArrowLeftOnRectangleIcon,
  UserCircleIcon,
  Bars3Icon,
  XMarkIcon,
  BuildingOfficeIcon,
  TableCellsIcon,
  UsersIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  ChevronRightIcon,
  RectangleStackIcon,
  UserGroupIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  ChatBubbleLeftRightIcon,
  TruckIcon,
  WrenchScrewdriverIcon,
  DocumentTextIcon,
  TrashIcon,
  GlobeAltIcon,
  Cog6ToothIcon,
  ShieldExclamationIcon,
  FlagIcon,
  ClipboardDocumentCheckIcon,
  AcademicCapIcon,
  ScaleIcon,
  PresentationChartLineIcon,
  ArrowPathIcon,
  InboxStackIcon,
  CalendarDaysIcon,
  FireIcon,
  KeyIcon,
  DocumentMagnifyingGlassIcon,
  BookOpenIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { ThemeToggle } from '../ui/theme-toggle';
import { UserAvatar } from '../UserAvatar';
import { Logger } from '../../lib/utils';
import { Footer } from '../Footer';
import { NotificationBell } from '../NotificationBell';
import {
  selectEffectivePlatformPermissions,
  selectPlatformPermissionsStatus,
} from '../../features/platformPermissions/platformPermissionsSlice';
import { hasPlatformEdit, hasPlatformVisibility } from '../../features/platformPermissions/platformAccessLevel';
import { CopilotPanel } from '../../features/ai/copilot/CopilotPanel';
import { InvestigatorPanel } from '../../features/ai/investigator/InvestigatorPanel';
import { useModuleAccess } from '../../features/organizationStandards/useModuleAccess';
import { selectEnabledStandards } from '../../features/organizationStandards/organizationStandardsSlice';
import { QMS_RECLAMOS_UI_ENABLED } from '../../features/qms/qmsFeatures';
import {
  loadTaxonomiesMenu,
  selectTaxonomiesTree,
  selectTaxonomiesStatus,
} from '../../features/formTaxonomies/formTaxonomiesSlice';
import type { FormTaxonomyNode, FormTemplateSummary, WorkbookPinSummary } from '../../features/formTaxonomies/types';
import type { AppDispatch } from '../../store/store';
import { deleteFormTemplate } from '../../features/formTemplates/formTemplatesApi';
import { deleteWorkbookMenuPin } from '../../features/workbooks/workbooksApi';

export const MainLayout = () => {
  const { confirm } = useConfirmDialog();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [investigatorOpen, setInvestigatorOpen] = useState(false);
  const effective = useSelector(selectEffectivePlatformPermissions);
  const permStatus = useSelector(selectPlatformPermissionsStatus);
  const canUseCopilot =
    permStatus === 'succeeded' && hasPlatformVisibility(effective?.['ai.assist.access']);
  const canUseInvestigator =
    permStatus === 'succeeded' && hasPlatformVisibility(effective?.['ai.investigate.access']);

  return (
    <div className='min-h-screen bg-background flex flex-col'>
      {/* Barra superior */}
      <header className='bg-card border-b border-border h-16 z-30 shadow-md px-4 md:px-6 sticky top-0 flex-shrink-0'>
        <div className='flex items-center justify-between h-full'>
          {/* Logo y toggle */}
          <div className='flex items-center gap-3'>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className='p-2 rounded-md text-foreground hover:bg-accent hover:text-accent-foreground transition-colors focus:outline-none'
              aria-label='Toggle menu'
            >
              {sidebarOpen ? <XMarkIcon className='h-5 w-5' /> : <Bars3Icon className='h-5 w-5' />}
            </button>
            <Link to='/' className='flex items-center gap-2'>
              <div className='bg-primary rounded-md p-1.5 shadow-sm'>
                <BuildingOfficeIcon className='h-5 w-5 text-primary-foreground' />
              </div>
              <span className='font-semibold text-lg text-foreground hidden md:block'>QMS</span>
            </Link>
          </div>

          {/* Menú Usuario (tenant oculto; se deriva de empresaId) */}
          <div className='flex items-center gap-3'>
            {canUseInvestigator && (
              <button
                type='button'
                onClick={() => {
                  setCopilotOpen(false);
                  setInvestigatorOpen((v) => !v);
                }}
                className={`p-2 rounded-md transition-colors focus:outline-none ${
                  investigatorOpen
                    ? 'bg-amber-600 text-white'
                    : 'text-foreground hover:bg-accent hover:text-accent-foreground'
                }`}
                aria-label='Abrir investigador SGI'
                aria-pressed={investigatorOpen}
                title='Investigador SGI'
              >
                <DocumentMagnifyingGlassIcon className='h-5 w-5' />
              </button>
            )}
            {canUseCopilot && (
              <button
                type='button'
                onClick={() => {
                  setInvestigatorOpen(false);
                  setCopilotOpen((v) => !v);
                }}
                className={`p-2 rounded-md transition-colors focus:outline-none ${
                  copilotOpen
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:bg-accent hover:text-accent-foreground'
                }`}
                aria-label='Abrir copiloto SGI'
                aria-pressed={copilotOpen}
              >
                <SparklesIcon className='h-5 w-5' />
              </button>
            )}
            <UserMenu />
          </div>
        </div>
      </header>

      <div className='flex flex-1'>
        {/* Sidebar - fixed para que se mantenga visible durante el scroll */}
        {sidebarOpen && (
        <aside
          style={{
            position: 'fixed',
              top: '4rem', // Debajo del header
              left: '0',
              width: '16rem',
            height: 'calc(100vh - 4rem)',
              zIndex: 10
          }}
            className='bg-card border-r border-border shadow-sm overflow-y-auto overflow-x-hidden'
        >
            <SidebarContent closeSidebar={() => setSidebarOpen(false)} />
        </aside>
        )}

        {/* Contenido principal - ocupa el espacio restante */}
        <main
          style={{
            marginLeft: sidebarOpen ? '16rem' : '0'
          }}
          className='flex-1 overflow-auto p-4 sm:p-6 md:p-8 w-full bg-background'
        >
          <div className='max-w-7xl mx-auto'>
            <Outlet />
          </div>
        </main>
      </div>

      {/* Footer - Fuera del contenedor principal, ancho completo */}
      <Footer />
      {canUseCopilot && (
        <CopilotPanel open={copilotOpen} onClose={() => setCopilotOpen(false)} />
      )}
      {canUseInvestigator && (
        <InvestigatorPanel open={investigatorOpen} onClose={() => setInvestigatorOpen(false)} />
      )}
    </div>
  );
};

// Menú de usuario en la cabecera
const UserMenu = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectCurrentUser);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  // Función para obtener la etiqueta de rol según el tipo de usuario
  const getRoleLabel = (role?: string): string => {
    if (!role) return 'Usuario';

    switch (role) {
      case 'superadmin':
        return 'Superadministrador';
      case 'admin':
        return 'Administrador';
      case 'user':
        return 'Usuario';
      default:
        return 'Usuario';
    }
  };

  return (
    <div className='relative flex items-center gap-2'>
      <NotificationBell />
      <ThemeToggle />

      <button
        className='flex items-center gap-2 p-2 rounded-full hover:bg-accent transition-colors'
        onClick={() => setMenuOpen(!menuOpen)}
      >
        <UserAvatar
          attachmentId={user?.profileImageAttachmentId}
          email={user?.email}
          className="h-8 w-8"
        />
        <span className='text-sm font-medium text-foreground hidden md:block'>
          {user?.email?.split('@')[0] || 'Usuario'}
        </span>
      </button>

      {menuOpen && (
        <>
          <div className='fixed inset-0 z-40' onClick={() => setMenuOpen(false)}></div>
          <div className='absolute right-0 mt-2 w-64 bg-card rounded-lg shadow-sm border border-border z-50 top-full'>
            <div className='p-4 border-b border-border bg-muted'>
              <p className='text-sm font-medium text-foreground'>{user?.email}</p>
              <p className='text-xs text-muted-foreground mt-1'>{getRoleLabel(user?.role)}</p>
            </div>
            <div className='p-2'>
              <Link
                to='/perfil'
                className='flex items-center gap-2 p-2.5 text-sm text-foreground hover:bg-accent hover:text-accent-foreground rounded-md transition-colors'
                onClick={() => setMenuOpen(false)}
              >
                <UserCircleIcon className='h-5 w-5 text-muted-foreground' />
                Mi Perfil
              </Link>
              <button
                onClick={handleLogout}
                className='w-full flex items-center gap-2 p-2.5 text-sm text-destructive hover:bg-destructive/10 rounded-md transition-colors mt-1'
              >
                <ArrowLeftOnRectangleIcon className='h-5 w-5' />
                Cerrar Sesión
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Contenido de la barra lateral
interface SidebarContentProps {
  closeSidebar: () => void;
}

const SidebarContent = ({ closeSidebar }: SidebarContentProps) => {
  const user = useSelector(selectCurrentUser);
  const effective = useSelector(selectEffectivePlatformPermissions);
  const permStatus = useSelector(selectPlatformPermissionsStatus);
  const { canAccess, ready: modulesReady } = useModuleAccess();
  const enabledStandards = useSelector(selectEnabledStandards);
  const taxonomiesTree = useSelector(selectTaxonomiesTree);
  const taxonomiesStatus = useSelector(selectTaxonomiesStatus);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const can = canAccess;
  const canEdit = (key: string) => hasPlatformEdit(effective?.[key]);
  const menuReady = permStatus === 'succeeded' && effective !== null && modulesReady;
  const canSeeFormTemplates = can('form_templates.access');
  const isSuperadmin = user?.role === 'superadmin';
  const surveysEnabled = import.meta.env.VITE_ENABLE_SURVEYS === 'true';
  const hasAssessmentForms =
    surveysEnabled && (can('assessments.surveys.access') || can('assessments.evaluations.access'));
  const hasDynamicFormTemplates = canSeeFormTemplates && taxonomiesTree.length > 0;
  const showFormsMenu = menuReady && (hasAssessmentForms || hasDynamicFormTemplates);

  useEffect(() => {
    if (canSeeFormTemplates && taxonomiesStatus === 'idle') {
      const roles = user?.role ? [user.role] : undefined;
      dispatch(loadTaxonomiesMenu(roles));
    }
  }, [canSeeFormTemplates, taxonomiesStatus, dispatch, user?.role]);

  const handleDeleteTemplate = useCallback(
    async (tpl: FormTemplateSummary) => {
      const msg = `¿Eliminar la plantilla "${tpl.name}" (${tpl.code})?\n\n` +
        `Se borra la definición y todas sus versiones, y se desactiva el ` +
        `Assessment espejo (las respuestas quedan archivadas).\n\n` +
        `La tabla en Teable NO se borra automáticamente: hay que eliminarla a ` +
        `mano en la UI de Teable.\n\nEsta acción no se puede deshacer.`;
      const ok = await confirm({
        title: 'Eliminar plantilla',
        message: msg,
        confirmText: 'Eliminar',
        variant: 'danger',
      });
      if (!ok) return;
      try {
        await deleteFormTemplate(tpl.code);
        const roles = user?.role ? [user.role] : undefined;
        await dispatch(loadTaxonomiesMenu(roles));
        if (location.pathname.startsWith(`/f/${tpl.code}`)) {
          navigate('/', { replace: true });
        }
      } catch (err) {
        Logger.debug('No se pudo eliminar la plantilla', { code: tpl.code, err });
        window.alert(`No se pudo eliminar la plantilla "${tpl.code}". Revisar logs.`);
      }
    },
    [confirm, dispatch, user?.role, location.pathname, navigate],
  );

  const handleDeleteWorkbookPin = useCallback(
    async (pin: WorkbookPinSummary) => {
      const label = pin.label || pin.workbook.name;
      const ok = await confirm({
        title: 'Quitar del menú',
        message: `¿Quitar "${label}" del menú dinámico?\n\nEl libro no se elimina.`,
        confirmText: 'Quitar',
        variant: 'danger',
      });
      if (!ok) return;
      try {
        await deleteWorkbookMenuPin(pin.workbookId, pin.id);
        const roles = user?.role ? [user.role] : undefined;
        await dispatch(loadTaxonomiesMenu(roles));
      } catch (err) {
        Logger.debug('No se pudo quitar el pin de libro', { pinId: pin.id, err });
        window.alert(`No se pudo quitar "${label}" del menú. Revisar logs.`);
      }
    },
    [confirm, dispatch, user?.role],
  );

  Logger.debug('Estado del usuario en Sidebar:', {
    role: user?.role,
  });

  return (
    <div className='py-6 px-3'>
      <nav className='space-y-6'>
        {/* Navegación principal */}
        <div className='px-4 py-4 space-y-1'>
          {permStatus === 'failed' && (
            <p className='px-4 py-2 text-xs text-destructive'>No se cargaron permisos. Usa Mi perfil o vuelve a entrar.</p>
          )}
          {permStatus !== 'failed' && !menuReady && (
            <p className='px-4 py-2 text-xs text-muted-foreground'>Cargando menú…</p>
          )}
          {menuReady && can('dashboard.access') && (
            <NavItem to='/' icon={HomeIcon} text='Dashboard' closeSidebar={closeSidebar} />
          )}
          {menuReady &&
            (can('communication_matrix.access') ||
              can('registros.non_conformities.access') ||
              (QMS_RECLAMOS_UI_ENABLED && can('registros.claims.access'))) && (
            <NavDriveSection
              label='Registros'
              icon={RectangleStackIcon}
              pathPrefixes={['/communication-matrix', '/registros']}
            >
              {can('communication_matrix.access') && (
                <NavSubItem
                  to='/communication-matrix'
                  icon={TableCellsIcon}
                  text='Matriz de Comunicación'
                  closeSidebar={closeSidebar}
                />
              )}
              {can('registros.non_conformities.access') && (
                <NavSubItem
                  to='/registros/no-conformidades'
                  icon={ExclamationTriangleIcon}
                  text='No conformidades'
                  closeSidebar={closeSidebar}
                />
              )}
              {QMS_RECLAMOS_UI_ENABLED && can('registros.claims.access') && (
                <NavSubItem
                  to='/registros/reclamos'
                  icon={ChatBubbleLeftRightIcon}
                  text='Reclamos'
                  closeSidebar={closeSidebar}
                />
              )}
            </NavDriveSection>
          )}
          {showFormsMenu && (
            <NavDriveSection
              label='Formularios'
              icon={ClipboardDocumentListIcon}
              pathPrefixes={[
                ...(surveysEnabled ? ['/surveys', '/evaluations', '/external-recipients'] : []),
                ...(hasDynamicFormTemplates ? ['/f'] : []),
              ]}
            >
              {surveysEnabled && can('assessments.surveys.access') && (
                <NavSubItem
                  to='/surveys'
                  icon={ClipboardDocumentListIcon}
                  text='Encuestas'
                  closeSidebar={closeSidebar}
                />
              )}
              {surveysEnabled && can('assessments.evaluations.access') && (
                <NavSubItem
                  to='/evaluations'
                  icon={ChartBarIcon}
                  text='Evaluaciones'
                  closeSidebar={closeSidebar}
                />
              )}
              {surveysEnabled && canEdit('assessments.surveys.access') && (
                <NavSubItem
                  to='/external-recipients'
                  icon={UserGroupIcon}
                  text='Destinatarios Externos'
                  closeSidebar={closeSidebar}
                />
              )}
              {hasDynamicFormTemplates && (
                <DynamicTaxonomiesSection
                  tree={taxonomiesTree}
                  closeSidebar={closeSidebar}
                  isSuperadmin={isSuperadmin}
                  onDeleteTemplate={handleDeleteTemplate}
                  onDeleteWorkbookPin={handleDeleteWorkbookPin}
                />
              )}
            </NavDriveSection>
          )}
          {menuReady &&
            (can('context.access') ||
              can('risks.access') ||
              can('org_units.access') ||
              can('risk_studies.access') ||
              can('risk_catalog.access') ||
              can('objectives.access') ||
              can('audits.access') ||
              can('competence.access') ||
              can('legal_requirements.access') ||
              can('management_review.access') ||
              can('improvement.suggestions.access') ||
              can('change_management.access') ||
              can('actions_inbox.access') ||
              can('sg_calendar.access')) && (
            <NavDriveSection
              label='Sistema de Gestión'
              icon={GlobeAltIcon}
              pathPrefixes={[
                '/contexto',
                '/objetivos',
                '/riesgos',
                '/niveles',
                '/estudios-riesgo',
                '/catalogo-riesgos',
                '/requisitos-legales',
                '/competencias',
                '/gestion-cambios',
                '/auditorias',
                '/revision-direccion',
                '/mejora/sugerencias',
                '/acciones',
                '/calendario-sg',
              ]}
            >
              {can('context.access') && (
                <NavSubItem
                  to='/contexto'
                  icon={GlobeAltIcon}
                  text='Contexto'
                  closeSidebar={closeSidebar}
                />
              )}
              {can('objectives.access') && (
                <NavSubItem
                  to='/objetivos'
                  icon={FlagIcon}
                  text='Objetivos'
                  closeSidebar={closeSidebar}
                />
              )}
              {can('risks.access') && (
                <NavSubItem
                  to='/riesgos'
                  icon={ShieldExclamationIcon}
                  text='Riesgos'
                  closeSidebar={closeSidebar}
                  end
                />
              )}
              {can('risks.access') && (
                <NavSubItem
                  to='/riesgos/tablero'
                  icon={ChartBarIcon}
                  text='Tablero MAP'
                  closeSidebar={closeSidebar}
                />
              )}
              {can('org_units.access') && (
                <NavSubItem
                  to='/niveles'
                  icon={BuildingOfficeIcon}
                  text='Niveles IPER'
                  closeSidebar={closeSidebar}
                />
              )}
              {can('risk_studies.access') && (
                <NavSubItem
                  to='/estudios-riesgo'
                  icon={TableCellsIcon}
                  text='Estudios IPER'
                  closeSidebar={closeSidebar}
                />
              )}
              {can('risk_catalog.access') && (
                <NavSubItem
                  to='/catalogo-riesgos'
                  icon={Cog6ToothIcon}
                  text='Catálogos riesgo'
                  closeSidebar={closeSidebar}
                />
              )}
              {can('legal_requirements.access') && (
                <NavSubItem
                  to='/requisitos-legales'
                  icon={ScaleIcon}
                  text='Requisitos legales'
                  closeSidebar={closeSidebar}
                />
              )}
              {can('competence.access') && (
                <NavSubItem
                  to='/competencias'
                  icon={AcademicCapIcon}
                  text='Competencias'
                  closeSidebar={closeSidebar}
                />
              )}
              {can('change_management.access') && (
                <NavSubItem
                  to='/gestion-cambios'
                  icon={ArrowPathIcon}
                  text='Gestión del cambio'
                  closeSidebar={closeSidebar}
                />
              )}
              {can('audits.access') && (
                <NavSubItem
                  to='/auditorias'
                  icon={ClipboardDocumentCheckIcon}
                  text='Auditorías'
                  closeSidebar={closeSidebar}
                />
              )}
              {can('management_review.access') && (
                <NavSubItem
                  to='/revision-direccion'
                  icon={PresentationChartLineIcon}
                  text='Revisión por la dirección'
                  closeSidebar={closeSidebar}
                />
              )}
              {can('improvement.suggestions.access') && (
                <NavSubItem
                  to='/mejora/sugerencias'
                  icon={SparklesIcon}
                  text='Sugerencias de mejora'
                  closeSidebar={closeSidebar}
                />
              )}
              {can('actions_inbox.access') && (
                <NavSubItem
                  to='/acciones'
                  icon={InboxStackIcon}
                  text='Bandeja de acciones'
                  closeSidebar={closeSidebar}
                />
              )}
              {can('sg_calendar.access') && (
                <NavSubItem
                  to='/calendario-sg'
                  icon={CalendarDaysIcon}
                  text='Calendario SG'
                  closeSidebar={closeSidebar}
                />
              )}
            </NavDriveSection>
          )}
          {menuReady &&
            (can('design_development.access') || can('nonconforming_output.access')) && (
            <NavDriveSection
              label='ISO 9001'
              icon={FlagIcon}
              pathPrefixes={['/iso9001/diseno', '/iso9001/salidas-no-conformes']}
            >
              {can('design_development.access') && (
                <NavSubItem
                  to='/iso9001/diseno'
                  icon={ClipboardDocumentListIcon}
                  text='Diseño y desarrollo'
                  closeSidebar={closeSidebar}
                />
              )}
              {can('nonconforming_output.access') && (
                <NavSubItem
                  to='/iso9001/salidas-no-conformes'
                  icon={ExclamationTriangleIcon}
                  text='Salidas no conformes'
                  closeSidebar={closeSidebar}
                />
              )}
            </NavDriveSection>
          )}
          {menuReady &&
            can('iso_implementation.access') &&
            enabledStandards?.includes('iso9001') && (
            <NavDriveSection
              label='ISO 9001 — Implementación'
              icon={FlagIcon}
              pathPrefixes={[
                '/iso9001/implementacion/avance',
                '/iso9001/implementacion/hoja-ruta',
                '/iso9001/implementacion/entregables',
                '/iso9001/implementacion/guia',
              ]}
            >
              <NavSubItem
                to='/iso9001/implementacion/avance'
                icon={ChartBarIcon}
                text='Avance'
                closeSidebar={closeSidebar}
              />
              <NavSubItem
                to='/iso9001/implementacion/guia'
                icon={BookOpenIcon}
                text='Guía de implementación'
                closeSidebar={closeSidebar}
              />
              <NavSubItem
                to='/iso9001/implementacion/hoja-ruta'
                icon={ClipboardDocumentListIcon}
                text='Hoja de ruta'
                closeSidebar={closeSidebar}
              />
              <NavSubItem
                to='/iso9001/implementacion/entregables'
                icon={DocumentTextIcon}
                text='Entregables'
                closeSidebar={closeSidebar}
              />
            </NavDriveSection>
          )}
          {menuReady && can('iso14001.aspects.access') && (
            <NavDriveSection
              label='ISO 14001'
              icon={GlobeAltIcon}
              pathPrefixes={['/iso14001/aspectos']}
            >
              <NavSubItem
                to='/iso14001/aspectos'
                icon={GlobeAltIcon}
                text='Aspectos ambientales'
                closeSidebar={closeSidebar}
              />
            </NavDriveSection>
          )}
          {menuReady &&
            can('iso_implementation.access') &&
            enabledStandards?.includes('iso14001') && (
            <NavDriveSection
              label='ISO 14001 — Implementación'
              icon={GlobeAltIcon}
              pathPrefixes={[
                '/iso14001/implementacion/avance',
                '/iso14001/implementacion/hoja-ruta',
                '/iso14001/implementacion/entregables',
                '/iso14001/implementacion/aia',
              ]}
            >
              <NavSubItem
                to='/iso14001/implementacion/avance'
                icon={ChartBarIcon}
                text='Avance'
                closeSidebar={closeSidebar}
              />
              <NavSubItem
                to='/iso14001/implementacion/hoja-ruta'
                icon={ClipboardDocumentListIcon}
                text='Hoja de ruta'
                closeSidebar={closeSidebar}
              />
              <NavSubItem
                to='/iso14001/implementacion/entregables'
                icon={DocumentTextIcon}
                text='Entregables'
                closeSidebar={closeSidebar}
              />
              <NavSubItem
                to='/iso14001/implementacion/aia'
                icon={DocumentMagnifyingGlassIcon}
                text='Matriz AIA'
                closeSidebar={closeSidebar}
              />
            </NavDriveSection>
          )}
          {menuReady &&
            (can('iso27001.assets.access') ||
              can('iso27001.soa.access') ||
              can('iso27001.incidents.access') ||
              can('security.audit.access') ||
              can('security.password_policy.access')) && (
            <NavDriveSection
              label='ISO 27001'
              icon={ShieldCheckIcon}
              pathPrefixes={[
                '/iso27001/activos',
                '/iso27001/soa',
                '/iso27001/incidentes',
                '/iso27001/bitacora',
                '/iso27001/politica-contrasenas',
              ]}
            >
              {can('iso27001.assets.access') && (
                <NavSubItem
                  to='/iso27001/activos'
                  icon={RectangleStackIcon}
                  text='Inventario de activos'
                  closeSidebar={closeSidebar}
                />
              )}
              {can('iso27001.soa.access') && (
                <NavSubItem
                  to='/iso27001/soa'
                  icon={ClipboardDocumentCheckIcon}
                  text='Declaración de Aplicabilidad'
                  closeSidebar={closeSidebar}
                />
              )}
              {can('iso27001.incidents.access') && (
                <NavSubItem
                  to='/iso27001/incidentes'
                  icon={ExclamationTriangleIcon}
                  text='Incidentes de seguridad'
                  closeSidebar={closeSidebar}
                />
              )}
              {can('security.audit.access') && (
                <NavSubItem
                  to='/iso27001/bitacora'
                  icon={DocumentMagnifyingGlassIcon}
                  text='Bitácora de seguridad'
                  closeSidebar={closeSidebar}
                />
              )}
              {can('security.password_policy.access') && (
                <NavSubItem
                  to='/iso27001/politica-contrasenas'
                  icon={KeyIcon}
                  text='Política de contraseñas'
                  closeSidebar={closeSidebar}
                />
              )}
            </NavDriveSection>
          )}
          {menuReady &&
            can('iso_implementation.access') &&
            enabledStandards?.includes('iso27001') && (
            <NavDriveSection
              label='ISO 27001 — Implementación'
              icon={ShieldCheckIcon}
              pathPrefixes={[
                '/iso27001/implementacion/avance',
                '/iso27001/implementacion/hoja-ruta',
                '/iso27001/implementacion/entregables',
                '/iso27001/soa',
                '/iso27001/implementacion/guia',
              ]}
            >
              <NavSubItem
                to='/iso27001/implementacion/avance'
                icon={ChartBarIcon}
                text='Avance'
                closeSidebar={closeSidebar}
              />
              <NavSubItem
                to='/iso27001/implementacion/guia'
                icon={BookOpenIcon}
                text='Guía de implementación'
                closeSidebar={closeSidebar}
              />
              <NavSubItem
                to='/iso27001/implementacion/hoja-ruta'
                icon={ClipboardDocumentListIcon}
                text='Hoja de ruta'
                closeSidebar={closeSidebar}
              />
              <NavSubItem
                to='/iso27001/implementacion/entregables'
                icon={ClipboardDocumentCheckIcon}
                text='Entregables'
                closeSidebar={closeSidebar}
              />
              <NavSubItem
                to='/iso27001/soa'
                icon={DocumentTextIcon}
                text='Anexo A (SoA)'
                closeSidebar={closeSidebar}
              />
            </NavDriveSection>
          )}
          {menuReady &&
            enabledStandards?.includes('iso45001') &&
            (can('iso45001.incidents.access') || can('iso45001.consultation.access')) && (
            <NavDriveSection
              label='ISO 45001'
              icon={ExclamationTriangleIcon}
              pathPrefixes={['/iso45001/incidentes', '/iso45001/consulta']}
            >
              {can('iso45001.incidents.access') && (
                <NavSubItem
                  to='/iso45001/incidentes'
                  icon={ShieldExclamationIcon}
                  text='Incidentes SST'
                  closeSidebar={closeSidebar}
                />
              )}
              {can('iso45001.consultation.access') && (
                <NavSubItem
                  to='/iso45001/consulta'
                  icon={ChatBubbleLeftRightIcon}
                  text='Consulta trabajadores'
                  closeSidebar={closeSidebar}
                />
              )}
            </NavDriveSection>
          )}
          {menuReady &&
            can('iso_implementation.access') &&
            enabledStandards?.includes('iso45001') && (
            <NavDriveSection
              label='ISO 45001 — Implementación'
              icon={ExclamationTriangleIcon}
              pathPrefixes={[
                '/iso45001/implementacion/avance',
                '/iso45001/implementacion/guia',
                '/iso45001/implementacion/hoja-ruta',
                '/iso45001/implementacion/entregables',
              ]}
            >
              <NavSubItem
                to='/iso45001/implementacion/avance'
                icon={ChartBarIcon}
                text='Avance'
                closeSidebar={closeSidebar}
              />
              <NavSubItem
                to='/iso45001/implementacion/guia'
                icon={BookOpenIcon}
                text='Guía de implementación'
                closeSidebar={closeSidebar}
              />
              <NavSubItem
                to='/iso45001/implementacion/hoja-ruta'
                icon={ClipboardDocumentListIcon}
                text='Hoja de ruta'
                closeSidebar={closeSidebar}
              />
              <NavSubItem
                to='/iso45001/implementacion/entregables'
                icon={DocumentTextIcon}
                text='Entregables'
                closeSidebar={closeSidebar}
              />
            </NavDriveSection>
          )}
          {menuReady && can('emergency.access') && (
            <NavDriveSection label='Emergencias' icon={FireIcon} pathPrefixes={['/emergencias']}>
              <NavSubItem
                to='/emergencias'
                icon={FireIcon}
                text='Planes y simulacros'
                closeSidebar={closeSidebar}
              />
            </NavDriveSection>
          )}
          {menuReady && can('iso_implementation.access') && (
            <NavDriveSection
              label='Ayuda'
              icon={BookOpenIcon}
              pathPrefixes={[
                '/ayuda/diccionario-iso',
                '/iso9001/implementacion/guia',
                '/iso27001/implementacion/guia',
                '/iso45001/implementacion/guia',
              ]}
            >
              <NavSubItem
                to='/ayuda/diccionario-iso'
                icon={BookOpenIcon}
                text='Diccionario ISO'
                closeSidebar={closeSidebar}
              />
              {enabledStandards?.includes('iso9001') && (
                <NavSubItem
                  to='/iso9001/implementacion/guia'
                  icon={ClipboardDocumentListIcon}
                  text='Guía ISO 9001'
                  closeSidebar={closeSidebar}
                />
              )}
              {enabledStandards?.includes('iso27001') && (
                <NavSubItem
                  to='/iso27001/implementacion/guia'
                  icon={ClipboardDocumentListIcon}
                  text='Guía ISO 27001'
                  closeSidebar={closeSidebar}
                />
              )}
              {enabledStandards?.includes('iso45001') && (
                <NavSubItem
                  to='/iso45001/implementacion/guia'
                  icon={ClipboardDocumentListIcon}
                  text='Guía ISO 45001'
                  closeSidebar={closeSidebar}
                />
              )}
            </NavDriveSection>
          )}
          {menuReady &&
            (can('suppliers.access') ||
              can('maintenance.access') ||
              can('documents.access') ||
              can('workbooks.access')) && (
            <NavDriveSection
              label='Operaciones'
              icon={Cog6ToothIcon}
              pathPrefixes={['/proveedores', '/mantenimiento', '/documentos', '/libros', '/catalogo/taxonomias']}
            >
              {can('suppliers.access') && (
                <NavSubItem
                  to='/proveedores'
                  icon={TruckIcon}
                  text='Proveedores'
                  closeSidebar={closeSidebar}
                />
              )}
              {can('maintenance.access') && (
                <NavSubItem
                  to='/mantenimiento/equipos'
                  icon={WrenchScrewdriverIcon}
                  text='Equipos / Mantenimiento'
                  closeSidebar={closeSidebar}
                />
              )}
              {can('maintenance.access') && (
                <NavSubItem
                  to='/mantenimiento/reportes'
                  icon={ChartBarIcon}
                  text='Reportes de mantenimiento'
                  closeSidebar={closeSidebar}
                />
              )}
              {can('documents.access') && (
                <NavSubItem
                  to='/documentos'
                  icon={DocumentTextIcon}
                  text='Control documental'
                  closeSidebar={closeSidebar}
                />
              )}
              {can('workbooks.access') && (
                <NavSubItem
                  to='/libros'
                  icon={TableCellsIcon}
                  text='Libros'
                  closeSidebar={closeSidebar}
                />
              )}
              {canEdit('form_templates.access') && (
                <NavSubItem
                  to='/catalogo/taxonomias'
                  icon={RectangleStackIcon}
                  text='Carpetas del menú'
                  closeSidebar={closeSidebar}
                />
              )}
            </NavDriveSection>
          )}
          {user?.role === 'superadmin' && (
            <NavDriveSection
              label='Administración'
              icon={ShieldCheckIcon}
              pathPrefixes={['/roles', '/users', '/permisos-plataforma', '/normas-iso']}
            >
              <NavSubItem to='/roles' icon={UsersIcon} text='Roles' closeSidebar={closeSidebar} />
              <NavSubItem to='/users' icon={UsersIcon} text='Usuarios' closeSidebar={closeSidebar} />
              <NavSubItem
                to='/permisos-plataforma'
                icon={ShieldCheckIcon}
                text='Permisos'
                closeSidebar={closeSidebar}
              />
              {can('admin.standards') && (
                <NavSubItem
                  to='/normas-iso'
                  icon={FlagIcon}
                  text='Normas ISO'
                  closeSidebar={closeSidebar}
                />
              )}
            </NavDriveSection>
          )}
        </div>

        {/* Sección de cuenta */}
        <div>
          <h3 className='px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider'>
            Cuenta
          </h3>
          <div className='mt-3 px-4 space-y-1'>
            {menuReady && can('user_groups.manage_own') && (
              <NavItem
                to='/mis-grupos'
                icon={UserGroupIcon}
                text='Mis grupos'
                closeSidebar={closeSidebar}
              />
            )}
            <NavItem to='/perfil' icon={UserCircleIcon} text='Mi Perfil' closeSidebar={closeSidebar} />
          </div>
        </div>
      </nav>

      {/* Footer con versión */}
      <div className='mt-auto'>
        <div className='border-t border-border pt-4'>
          <p className='text-xs text-muted-foreground text-center'>QMS v1.0</p>
        </div>
      </div>
    </div>
  );
};

function pathMatchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

function getTaxonomyNodePathPrefixes(node: FormTaxonomyNode): string[] {
  const templatePaths = node.templates.map((tpl) => `/f/${tpl.code}`);
  const workbookPaths = node.workbookPins.map((pin) => `/libros/${pin.workbookId}`);
  const childPaths = node.children.flatMap((child) => getTaxonomyNodePathPrefixes(child));
  return [...templatePaths, ...workbookPaths, ...childPaths];
}

interface NavDriveSectionProps {
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  pathPrefixes: string[];
  children: React.ReactNode;
}

const NavDriveSection = ({
  label,
  icon: Icon,
  pathPrefixes,
  children,
}: NavDriveSectionProps) => {
  const location = useLocation();
  const onChildPath = pathPrefixes.some((p) => pathMatchesPrefix(location.pathname, p));
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (onChildPath) {
      setExpanded(true);
    }
  }, [onChildPath]);

  const open = onChildPath || expanded;

  return (
    <div className='rounded-md'>
      <button
        type='button'
        aria-expanded={open}
        onClick={() => setExpanded((v) => !v)}
        className='w-full flex items-center gap-1 px-4 py-3 rounded-md text-left text-foreground hover:bg-muted transition-colors'
      >
        <ChevronRightIcon
          className={`h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`}
        />
        <Icon className='h-5 w-5 flex-shrink-0 text-muted-foreground' />
        <span className='text-sm font-medium'>{label}</span>
      </button>
      {open && (
        <div className='ml-3 pl-2 border-l border-border space-y-0.5 pb-1'>{children}</div>
      )}
    </div>
  );
};

interface NavSubItemProps {
  to: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  text: string;
  closeSidebar: () => void;
  end?: boolean;
}

const NavSubItem = ({ to, icon: Icon, text, closeSidebar, end = false }: NavSubItemProps) => {
  const handleClick = () => {
    if (window.innerWidth < 1024) {
      closeSidebar();
    }
  };

  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex items-center gap-3 pl-3 pr-3 py-2 rounded-md text-sm transition-colors ${
          isActive
            ? 'bg-accent text-accent-foreground font-medium'
            : 'text-foreground hover:bg-muted/80'
        }`
      }
      onClick={handleClick}
    >
      {({ isActive }) => (
        <>
          <Icon
            className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-primary' : 'text-muted-foreground'}`}
          />
          <span>{text}</span>
        </>
      )}
    </NavLink>
  );
};

// Item de navegación en la sidebar
interface NavItemProps {
  to: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  text: string;
  badge?: string;
  closeSidebar: () => void;
}

const NavItem = ({ to, icon: Icon, text, badge, closeSidebar }: NavItemProps) => {
  // Cerrar el sidebar en dispositivos móviles cuando se hace clic en un enlace
  const handleClick = () => {
    if (window.innerWidth < 1024) {
      closeSidebar();
    }
  };

  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex items-center justify-between px-4 py-3 rounded-md transition-colors ${
          isActive
            ? 'bg-accent text-accent-foreground font-medium'
            : 'text-foreground hover:bg-muted'
        }`
      }
      onClick={handleClick}
    >
      {({ isActive }) => (
        <>
          <div className='flex items-center'>
            <Icon
              className={`h-5 w-5 mr-3 flex-shrink-0 ${
                isActive ? 'text-primary' : 'text-muted-foreground'
              }`}
            />
            <span className='text-sm'>{text}</span>
          </div>

          {badge && (
            <span className='ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary'>
              {badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );
};

interface DynamicTaxonomiesSectionProps {
  tree: FormTaxonomyNode[];
  closeSidebar: () => void;
  isSuperadmin: boolean;
  onDeleteTemplate: (tpl: FormTemplateSummary) => void;
  onDeleteWorkbookPin: (pin: WorkbookPinSummary) => void;
}

const DynamicTaxonomiesSection = ({
  tree,
  closeSidebar,
  isSuperadmin,
  onDeleteTemplate,
  onDeleteWorkbookPin,
}: DynamicTaxonomiesSectionProps) => {
  return (
    <>
      {tree.map((node) => (
        <DynamicTaxonomyNode
          key={node.id}
          node={node}
          closeSidebar={closeSidebar}
          isSuperadmin={isSuperadmin}
          onDeleteTemplate={onDeleteTemplate}
          onDeleteWorkbookPin={onDeleteWorkbookPin}
        />
      ))}
    </>
  );
};

interface DynamicTaxonomyNodeProps {
  node: FormTaxonomyNode;
  closeSidebar: () => void;
  isSuperadmin: boolean;
  onDeleteTemplate: (tpl: FormTemplateSummary) => void;
  onDeleteWorkbookPin: (pin: WorkbookPinSummary) => void;
}

const DynamicTaxonomyNode = ({
  node,
  closeSidebar,
  isSuperadmin,
  onDeleteTemplate,
  onDeleteWorkbookPin,
}: DynamicTaxonomyNodeProps) => {
  const hasContent = node.templates.length > 0 || node.workbookPins.length > 0 || node.children.length > 0;
  if (!hasContent) return null;

  return (
    <NavDriveSection
      label={node.name}
      icon={ClipboardDocumentListIcon}
      pathPrefixes={getTaxonomyNodePathPrefixes(node)}
    >
      {node.templates.map((tpl) => (
        <TemplateSubItemRow
          key={tpl.id}
          tpl={tpl}
          closeSidebar={closeSidebar}
          isSuperadmin={isSuperadmin}
          onDelete={onDeleteTemplate}
        />
      ))}
      {node.workbookPins.map((pin) => (
        <WorkbookPinSubItemRow
          key={pin.id}
          pin={pin}
          closeSidebar={closeSidebar}
          isSuperadmin={isSuperadmin}
          onDelete={onDeleteWorkbookPin}
        />
      ))}
      {node.children.map((child) => (
        <DynamicTaxonomyNode
          key={child.id}
          node={child}
          closeSidebar={closeSidebar}
          isSuperadmin={isSuperadmin}
          onDeleteTemplate={onDeleteTemplate}
          onDeleteWorkbookPin={onDeleteWorkbookPin}
        />
      ))}
    </NavDriveSection>
  );
};

interface TemplateSubItemRowProps {
  tpl: FormTemplateSummary;
  closeSidebar: () => void;
  isSuperadmin: boolean;
  onDelete: (tpl: FormTemplateSummary) => void;
}

/**
 * Fila de un template dentro del menú dinámico.
 * Renderiza el link de navegación y, solo si el usuario es superadmin, un
 * botón papelera que dispara el borrado. El botón vive fuera del NavLink
 * para evitar interactive-nesting (button dentro de un anchor).
 */
const TemplateSubItemRow = ({ tpl, closeSidebar, isSuperadmin, onDelete }: TemplateSubItemRowProps) => {
  if (!isSuperadmin) {
    return (
      <NavSubItem
        to={`/f/${tpl.code}`}
        icon={ClipboardDocumentListIcon}
        text={tpl.name}
        closeSidebar={closeSidebar}
      />
    );
  }

  return (
    <div className='group relative flex items-center gap-1'>
      <div className='flex-1 min-w-0'>
        <NavSubItem
          to={`/f/${tpl.code}`}
          icon={ClipboardDocumentListIcon}
          text={tpl.name}
          closeSidebar={closeSidebar}
        />
      </div>
      <button
        type='button'
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDelete(tpl);
        }}
        className='opacity-0 group-hover:opacity-100 focus:opacity-100 p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all'
        title={`Eliminar plantilla ${tpl.code} (solo superadmin)`}
        aria-label={`Eliminar plantilla ${tpl.code}`}
      >
        <TrashIcon className='h-4 w-4' />
      </button>
    </div>
  );
};

interface WorkbookPinSubItemRowProps {
  pin: WorkbookPinSummary;
  closeSidebar: () => void;
  isSuperadmin: boolean;
  onDelete: (pin: WorkbookPinSummary) => void;
}

const WorkbookPinSubItemRow = ({ pin, closeSidebar, isSuperadmin, onDelete }: WorkbookPinSubItemRowProps) => {
  const label = pin.label || pin.workbook.name;
  if (!isSuperadmin) {
    return (
      <NavSubItem
        to={`/libros/${pin.workbookId}`}
        icon={TableCellsIcon}
        text={label}
        closeSidebar={closeSidebar}
      />
    );
  }

  return (
    <div className='group relative flex items-center gap-1'>
      <div className='flex-1 min-w-0'>
        <NavSubItem
          to={`/libros/${pin.workbookId}`}
          icon={TableCellsIcon}
          text={label}
          closeSidebar={closeSidebar}
        />
      </div>
      <button
        type='button'
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDelete(pin);
        }}
        className='opacity-0 group-hover:opacity-100 focus:opacity-100 p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all'
        title={`Quitar libro ${label} del menú (solo superadmin)`}
        aria-label={`Quitar libro ${label} del menú`}
      >
        <TrashIcon className='h-4 w-4' />
      </button>
    </div>
  );
};
